import { h, Component } from 'preact';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import Store from 'store/store';
import { joinClasses } from 'utils/utils';
import { setTicketStatus } from 'utils/tickets';
import { trackGAEvent } from 'utils/track';

import TheaterInfo from 'components/theaterInfo/theaterInfo';
import OnlineIndicator from 'components/onlineIndicator/onlineIndicator';
import CapacityInfo from 'components/capacityInfo/capacityInfo';
import IconTick from 'components/icons/tick';
import IconInvalid from 'components/icons/invalid';
import IconSearch from 'components/icons/search';

import s from 'components/scanner/scanner.scss';

export default class Scanner extends Component {
	constructor(props) {
		super(props);
		this.goToHome = this.goToHome.bind(this);
		this.goToSearch = this.goToSearch.bind(this);
		this.onQRScan = this.onQRScan.bind(this);
		this.resumeScanner = this.resumeScanner.bind(this);
		this.undoLastTicket = this.undoLastTicket.bind(this);
	}

	componentDidMount() {
		if (!this.props.camera) {
			this.checkCameras();
		}
	}

	goToHome() {
		this.props.goTo?.('main');
	}
	goToSearch() {
		trackGAEvent('scan-search-cta', 'scanQrCode', '');
		this.props.goTo?.('search');
	}

	async checkCameras() {
		try {
			const cameras = await Html5Qrcode.getCameras();
			if (!cameras?.length) {
				throw new Error('No cameras found');
			}
			const camera = cameras.find(camera => /back/i.test(camera.label)) || cameras[0];
			if (!camera?.id) {
				throw new Error('No valid camera found');
			}
			Store.get().set({ cameraState: 'allowed', camera: camera.id });
			trackGAEvent('scan-allow-camera-permissions', 'scanQrCode', '');
		} catch (err) {
			console.log('CAMERA ERROR', err);
			Store.get().set({ cameraState: 'denied' });
			trackGAEvent('scan-denied-camera-permissions', 'scanQrCode', '');
		}
	}

	playSound(type = 'success') {
		const audio = document.getElementById(`sound-${type}`);
		if (audio) {
			audio.currentTime = 0;
			audio.play();
		}
	}

	autoCloseConfirmationIn(time) {
		// clearInterval(this.autoCloseRefreshTimer);
		clearTimeout(this.autoCloseConfirmationTimer);
		this.setState({ confirmationClosesAt: Date.now() + time });
		// this.autoCloseRefreshTimer = setInterval(() => this.forceUpdate(), 1000);
		this.autoCloseConfirmationTimer = setTimeout(() => {
			// clearInterval(this.autoCloseRefreshTimer);
			// this.setState({ showConfirmation: false });
			this.resumeScanner();
		}, time);
	}

	async onQRScan(text, result) {
		if (!this.props.tickets) {
			return;
		}
		// clearInterval(this.autoCloseRefreshTimer);
		clearTimeout(this.autoCloseConfirmationTimer);
		await this.$scanner.pause();
		let code = text;
		try {
			let url = new URL(text);
			let match = url.hash.match(/^#ticket=(.+)$/i);
			code = match[1];
		} catch (err) {}
		let ticket = this.props.tickets.find(ticket => ticket.shortcode === code);
		if (ticket) {
			if (ticket.status === 'admitted') {
				this.setState({ showConfirmation: true, confirmationData: { type: 'alreadyAdmitted', ticket } });
				this.playSound('error');
				trackGAEvent('scan-qrcode-already-checkedin', 'scanQrCode', code);
			} else {
				ticket = setTicketStatus(ticket, 'admitted');
				this.setState({ showConfirmation: true, confirmationData: { type: 'success', ticket } });
				this.playSound('success');
				this.autoCloseConfirmationIn(2000);
				trackGAEvent('scan-qrcode-success', 'scanQrCode', code);
			}
		} else {
			this.setState({ showConfirmation: true, confirmationData: { type: 'invalid' } });
			this.playSound('error');
			trackGAEvent('scan-qrcode-fail', 'scanQrCode', code);
		}
	}

	async resumeScanner() {
		// clearInterval(this.autoCloseRefreshTimer);
		clearTimeout(this.autoCloseConfirmationTimer);
		await this.$scanner.resume();
		this.setState({ showConfirmation: false });
	}

	async undoLastTicket() {
		const ticket = this.state.confirmationData.ticket;
		setTicketStatus(ticket, 'absent');
		trackGAEvent('absent', 'tickets', '');
		return this.resumeScanner();
	}

	render(props, state) {
		const { theater, movie, screening, tickets, offline, cameraState, camera } = props;
		const { showConfirmation, confirmationData } = state;

		if (cameraState === 'unknown') {
			return (
				<div class={s.requestingCamera}>
					<div class={s.prompt}>Please allow camera permission</div>
				</div>
			);
		} else if (cameraState === 'denied' || !camera) {
			return (
				<div class={s.cameraDenied}>
					<div class={s.message}>Camera access is required to scan tickets</div>
					<div class={s.help}>Please Enable Access In Device Settings</div>
					<button class={s.back} onClick={this.goToHome}>HOME</button>
				</div>
			);
		}

		let confirmationContent;
		if (confirmationData?.type === 'success') {
			// const autoCloseIn = Math.ceil(Math.max(0, (state.confirmationClosesAt || 0) - Date.now()) / 1000);
			confirmationContent = [
				<div class={s.name} key="name">{confirmationData.ticket?.name}</div>,
				<IconTick class={s.icon} key="icon" />,
				<div class={s.message} key="message">ADMITTED</div>,
				<button class={s.ok} onClick={this.resumeScanner} key="confirm">OK</button>,
				<button class={s.undo} onClick={this.undoLastTicket} key="undo">UNDO</button>,
			];
		} else if (confirmationData?.type === 'alreadyAdmitted') {
			confirmationContent = [
				<div class={s.name} key="name">{confirmationData.ticket?.name}</div>,
				<IconInvalid class={s.icon} key="icon" />,
				<div class={s.message} key="message">ALREADY ADMITTED</div>,
				<button class={s.ok} onClick={this.resumeScanner} key="confirm">OK</button>,
			];
		} else if (confirmationData?.type === 'invalid') {
			confirmationContent = [
				<IconInvalid class={s.icon} key="icon" />,
				<div class={s.message} key="message">INVALID</div>,
				<div class={s.details} key="details">QR CODE NOT RECOGNIZED</div>,
				<button class={s.ok} onClick={this.resumeScanner} key="confirm">OK</button>,
			];
		}

		return (
			<div class={s.root}>
				<TheaterInfo theater={theater} movie={movie} screening={screening} mini class={s.theaterInfo} />
				<div class={s.scannerContainer}>
					<ScannerElement class={s.scanner} onScan={this.onQRScan} camera={camera} ref={e => this.$scanner = e} />
					<div class={joinClasses(s.confirmation, s[confirmationData?.type], showConfirmation && s.show)}>
						{confirmationContent}
					</div>
					<OnlineIndicator offline={offline} />
				</div>
				<CapacityInfo screening={screening} tickets={tickets} offline={offline} />
				<button class={s.fakeSearch} onClick={this.goToSearch}>
					<span>Search</span>
					<div class={s.fakeSearchSubmit}><IconSearch /></div>
				</button>
			</div>
		);
	}
}

class ScannerElement extends Component {
	constructor(props) {
		super(props);
		this.onScan = this.onScan.bind(this);
		this.onError = this.onError.bind(this);
		this.id = `scanner-${Date.now()}`;
	}

	shouldComponentUpdate() {
		return false;
	}

	async componentDidMount() {
		this.scanner = new Html5Qrcode(this.id, {
			formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
			useBarCodeDetectorIfSupported: true,
		});
		await this.scanner.start(this.props.camera,
			{
				fps: 15,
			}, this.onScan, this.onError);
		this.scanner.applyVideoConstraints({ focusMode: 'continuous', exposureMode: 'continuous', whiteBalanceMode: 'continuous' });
	}

	async componentWillUnmount() {
		try {
			const element = this.base.firstElementChild;
			element.style.display = 'none';
			// Avoid it being removed from the DOM, causing issues in .stop()
			document.body.appendChild(element);
			await this.scanner?.stop();
			await this.scanner?.clear();
			element.remove();
		} catch (e) {}
	}

	onScan(text, result) {
		this.props.onScan?.(text, result, this.scanner);
	}

	onError(message, error) {
		this.props.onError?.(message, error, this.scanner);
	}

	pause() {
		trackGAEvent('scan-camera-pause', 'scanQrCode', '');
		return this.scanner?.pause();
	}
	resume() {
		return this.scanner?.resume();
	}

	render(props) {
		const { camera, onScan, onError, ...rest } = props;
		// Need an extra wrapper to avoid issues when stopping (needs to be in the DOM)
		return (
			<div class={s.scannerWrapper}>
				<div {...rest} id={this.id} />
			</div>
		);
	}
}